import React, { ChangeEvent, useEffect, useState } from "react";
import { List, AutoSizer, ListRowRenderer } from 'react-virtualized';

import { FormElementWrapperProps, TextInputProps, SelectDropdownProps, ISelectDropdownOption, SearchSelectProps, TextAreaProps } from "../../interfaces/form_elements";

import OutsideAlerter from "../wrappers/OutsideAlerter";
import { SVGIcon } from "../wrappers/SVGIcon";

import { Icons } from "../../data/Assets";

import { getCanadianDollarsString, getElipsisString, getPhoneNumberString } from "../../helpers/utility";

const FormElementWrapper = (props: FormElementWrapperProps) => {
    const { className, length_flex = 1, children, label, label_info, required, errorMsg } = props;

    return (
        <div className={`immployer_admin__form_element_wrapper immployer_admin__form_element_wrapper__flex_${length_flex} ${className}`}>
            {!!label &&
                <label>
                    <span>{label}</span>
                    {!!label_info && <span className="label_info">&nbsp;{label_info}</span>}
                    {!!required && <span className="color_error">&nbsp;*</span>}
                </label>
            }
            {children}
            {errorMsg && errorMsg?.length > 0 && <div className="error_text">{errorMsg}</div>}
        </div>
    );
};

export const TextInput = (props: TextInputProps) => {
    const { name, type, pattern_mask, max_length, read_only, label, label_info, required, errorMsg, placeholder, onChange, value } = props;

    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isPreSecVisible, setIsPreSecVisible] = useState(false);
    const [isPostSecVisible, setIsPostSecVisible] = useState(false);

    useEffect(() => {
        if (type === "password") {
            setIsPostSecVisible(true);
        };
    }, [type]);

    useEffect(() => {
        if (pattern_mask === "currency_cad") {
            setIsPreSecVisible(true);
        };
    }, [pattern_mask]);

    const onClickInputButton = () => {
        if (type === 'password') {
            setIsPasswordVisible(!isPasswordVisible);
        };
    };

    const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
        if (read_only) return;

        let value = e.target.value;

        if (pattern_mask === 'integer') {
            value = value.replace(/[^0-9]/g, '');
        } else if (pattern_mask === 'float') {
            value = value.replace(/[^0-9.]/g, '');
            value = value.replace(/\.(?=.*\.)/, '');
        } else if (pattern_mask === "currency_cad") {
            value = getCanadianDollarsString(value);
        } else if (pattern_mask === "phone_number") {
            value = getPhoneNumberString(value);
        } else if (pattern_mask === "doc_number") {
            value = value.replace(/[^a-zA-Z0-9]/g, '');
            value = value.toUpperCase();
        }

        if (max_length) {
            value = value.slice(0, max_length);
        }

        onChange(value);
    };

    return (
        <FormElementWrapper
            className={`immployer_admin__text_input_wrapper`}
            label={label}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
        >
            <div className={`input_group ${read_only ? 'input_group__readonly' : ''}`}>
                {isPreSecVisible &&
                    <div className="input_group__pre_sec">
                        {pattern_mask === "currency_cad" && <div className="color_dark">$</div>}
                    </div>
                }
                <input
                    name={name}
                    type={type === "password" && isPasswordVisible ? "text" : type}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChangeInput}
                    readOnly={!!read_only}
                />
                {isPostSecVisible &&
                    <div className="input_group__post_sec">
                        {type === "password" &&
                            <button onClick={onClickInputButton} type="button">
                                <SVGIcon src={isPasswordVisible ? Icons.EyeFill : Icons.EyeOffFill} />
                            </button>
                        }
                    </div>
                }
            </div>
        </FormElementWrapper>
    );
};

export const TextAreaInput = (props: TextAreaProps) => {
    const { name, max_length, read_only, label, label_info, required, errorMsg, placeholder, onChange, value } = props;

    const onChangeInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (read_only) return;

        let value = e.target.value;

        if (max_length) {
            value = value.slice(0, max_length);
        };

        onChange(value);
    };

    return (
        <FormElementWrapper
            className={`immployer_admin__text_input_wrapper`}
            label={label}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
        >
            <div className={`input_group ${read_only ? 'input_group__readonly' : ''}`}>
                <textarea
                    name={name}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChangeInput}
                    readOnly={!!read_only}
                    rows={4}
                />
            </div>
        </FormElementWrapper>
    );
};

export const SelectDropdown = (props: SelectDropdownProps) => {
    const { read_only, length_flex = 1, label, label_info, required, errorMsg, placeholder, onChange, value, options } = props;

    const [open, setOpen] = useState(false);

    const onToggleDropdown = () => {
        if (!options.length || read_only) return;
        setOpen(!open);
    };

    const onChangeSelect = (option: ISelectDropdownOption) => {
        if (read_only) return;
        setOpen(false);
        onChange(option);
    };

    return (
        <FormElementWrapper
            className={`immployer_admin__select_dropdown_wrapper`}
            label={label}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
            length_flex={length_flex}
        >
            <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
                <div className={`immployer_admin__select_dropdown__container ${open ? 'immployer_admin__select_dropdown__container__opened' : ''}`}>
                    <button
                        className={`immployer_admin__select_dropdown__btn ${read_only ? 'immployer_admin__select_dropdown__btn__readonly' : ''}`}
                        onClick={onToggleDropdown}
                    >
                        {
                            value.label?.length
                                ? <span className="color_dark">{getElipsisString(value.label, 100)}</span>
                                : <span className="color_grey">{placeholder?.length ? placeholder : '-- Select --'}</span>
                        }
                        <div className="icons_sec">
                            <SVGIcon src={Icons.TriangleUpDownFill} color={read_only ? 'grey' : 'primary'} />
                        </div>
                    </button>
                    {open &&
                        <div className="immployer_admin__ssd_dropdown__content">
                            {(!!placeholder?.length && !required) &&
                                <button onClick={() => onChangeSelect({ value: "", label: "" })}>{placeholder}</button>
                            }
                            {options.map((option, index) =>
                                <button
                                    key={index}
                                    className={option.readOnly ? 'immployer_admin__ssd_dropdown__option__readonly' : ''}
                                    onClick={() => option.readOnly ? null : onChangeSelect(option)}
                                >
                                    {option.label}
                                </button>
                            )}
                        </div>
                    }
                </div>
            </OutsideAlerter>
        </FormElementWrapper>
    );
};

export const SearchSelect = (props: SearchSelectProps) => {
    const { read_only, length_flex = 1, label, label_info, required, errorMsg, placeholder, onChange, value, options } = props;

    const [open, setOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState<ISelectDropdownOption[]>(options);
    const [search, setSearch] = useState("");

    useEffect(() => {
        setSearch(value.label);

        // eslint-disable-next-line
    }, [value]);

    const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        if (read_only) return;
        setOpen(true);

        const val = e.target.value;
        setSearch(val);

        const filteredOptions = options.filter(option => option.label.toLowerCase().includes(val.toLowerCase()));
        setFilteredOptions(filteredOptions);
    };

    const onChangeSelect = (option: ISelectDropdownOption) => {
        if (read_only) return;

        setOpen(false);
        onChange(option);
    };

    const onBlurInput = () => {
        if (read_only) return;

        const option = options.find(option => option.label === search);
        if (option) {
            onChange(option);
        } else {
            onChange({ value: "", label: "" });
        };
    };

    const rowRenderer: ListRowRenderer = ({ index, style }) => {
        return (
            <div
                key={index}
                style={{
                    ...style,
                    whiteSpace: "pre",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%"
                }}
            >
                <button onClick={() => onChangeSelect(filteredOptions[index])}>
                    {filteredOptions[index].label}
                </button>
            </div>
        );
    };

    return (
        <FormElementWrapper
            className={`immployer_admin__search_select_wrapper`}
            label={label}
            label_info={label_info}
            required={required}
            errorMsg={errorMsg}
            length_flex={length_flex}
        >
            <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
                <div className={`immployer_admin__search_select__container ${open ? 'immployer_admin__search_select__container__opened' : ''}`}>
                    <div className={`immployer_admin__search_select__input_group ${!!read_only ? 'immployer_admin__search_select__input_group__readonly' : ''}`}>
                        <input
                            type="text"
                            className={`immployer_admin__search_select__input`}
                            onChange={onChangeSearch}
                            onBlur={onBlurInput}
                            placeholder={placeholder}
                            value={search}
                        />
                        <SVGIcon src={Icons.Search} color={read_only ? 'grey' : 'primary'} />
                    </div>
                    {open &&
                        <div className="immployer_admin__ssd_dropdown__virtualized_content">
                            {
                                filteredOptions.length === 0
                                    ? <div><div><button>No records found!</button></div></div>
                                    : <AutoSizer disableWidth>
                                        {() => {
                                            return (
                                                <List
                                                    width={1}
                                                    height={filteredOptions.length > 10 ? 400 : filteredOptions.length * 40}
                                                    overscanRowsCount={2}
                                                    rowCount={filteredOptions.length}
                                                    rowHeight={40}
                                                    rowRenderer={rowRenderer}
                                                    containerStyle={{ width: "100%", maxWidth: "100%" }}
                                                    style={{ width: "100%" }}
                                                />
                                            );
                                        }}
                                    </AutoSizer>
                            }
                        </div>
                    }
                </div>
            </OutsideAlerter>
        </FormElementWrapper>
    );
};